/**
 * Auth port and strategy factory. The app depends only on `AuthStrategy`: native and federated use
 * AmplifyAuthStrategy, direct-oidc uses DirectOidcAuthStrategy.
 */

import { getConfig } from "../config"
import { AmplifyAuthStrategy } from "./authAmplify"
import { DirectOidcAuthStrategy } from "./authDirectOidc"

/**
 * The signed-in user, independent of auth mode.
 */
export interface UserInfo {
  /**
   * Login identifier (IDP claim such as Entra `oid` in direct-oidc, else the Cognito username) used
   * to key client caches. Not the backend `principal_id`; use `email` for display.
   */
  principalId: string
  email?: string
  isAuthenticated: boolean
}

/**
 * Auth event callbacks that AuthContext supplies, so adapters never touch React or routing.
 */
export interface AuthEventHandlers {
  /**
   * A redirect sign-in completed.
   */
  onSignedIn: (user: UserInfo) => void
  /**
   * A redirect sign-in failed; the message is safe to show.
   */
  onSignInError: (message: string) => void
  /**
   * A session became unrecoverable, e.g. the IDP rejected a refresh.
   */
  onSessionLost: () => void
}

/**
 * The auth port, implemented once per auth mode.
 */
export interface AuthStrategy {
  /**
   * Username/password sign-in (native mode). Throws in redirect-only modes.
   */
  login(username: string, password: string): Promise<UserInfo>
  /**
   * Begin a redirect/SSO sign-in. Throws in password-only modes.
   */
  loginWithRedirect(): Promise<void>
  /**
   * Complete a redirect sign-in. Return null when sign-in arrives through `subscribe` instead
   * (Amplify Hub). Throw a message safe to show on failure.
   */
  completeRedirectCallback(): Promise<UserInfo | null>
  /**
   * Current signed-in user, or null if none.
   */
  getCurrentUserInfo(): Promise<UserInfo | null>
  /**
   * Bearer token to attach to API requests.
   */
  getApiToken(): Promise<string>
  /**
   * Sign out locally.
   */
  logout(): Promise<void>
  /**
   * Subscribe to auth lifecycle events. Returns an unsubscribe function.
   */
  subscribe(handlers: AuthEventHandlers): () => void
}

let _strategy: AuthStrategy | null = null

function createStrategy(): AuthStrategy {
  const mode = getConfig().authMode
  switch (mode) {
    case "native":
    case "federated":
      return new AmplifyAuthStrategy()
    case "direct-oidc":
      return new DirectOidcAuthStrategy()
    default:
      // Fail on an unknown mode rather than guess a strategy.
      throw new Error(`Unsupported authMode: ${String(mode satisfies never)}`)
  }
}

/**
 * Return the memoized strategy for the runtime auth mode. Load config first.
 */
export function getAuthStrategy(): AuthStrategy {
  if (!_strategy) {
    _strategy = createStrategy()
  }
  return _strategy
}
